"use client";

import { c as _c } from "react/compiler-runtime";
import type { InitialConfigType } from "@lexical/react/LexicalComposer";
import type { FocusEvent } from "react";
import { LexicalComposer } from "@lexical/react/LexicalComposer";
import { forwardRef, useImperativeHandle, useCallback, useEffect, useRef } from "react";
import type { EditorState } from "lexical";
import { RootNode, COMMAND_PRIORITY_CRITICAL, createCommand } from "lexical";
import { RichTextPlugin } from "@lexical/react/LexicalRichTextPlugin";
import { ContentEditable } from "@lexical/react/LexicalContentEditable";
import LexicalErrorBoundary from "@lexical/react/LexicalErrorBoundary";
import { HistoryPlugin } from "@lexical/react/LexicalHistoryPlugin";
import { OnChangePlugin } from "@lexical/react/LexicalOnChangePlugin";
import { MarkdownShortcutPlugin } from "@lexical/react/LexicalMarkdownShortcutPlugin";
import { TabIndentationPlugin } from "@lexical/react/LexicalTabIndentationPlugin";
import { ListNode, ListItemNode } from "@lexical/list";
import { HeadingNode, QuoteNode } from "@lexical/rich-text";
import { HorizontalRuleNode } from "@lexical/react/LexicalHorizontalRuleNode";
import { ListPlugin } from "@lexical/react/LexicalListPlugin";
import { TableCellNode, TableNode, TableRowNode } from "@lexical/table";
import { CodeHighlightNode, CodeNode } from "@lexical/code";
import { AutoLinkNode, LinkNode } from "@lexical/link";
import { LinkPlugin } from "@lexical/react/LexicalLinkPlugin";
import type { MentionedUser, MentionedWave, ReferencedNft } from "@/entities/IDrop";
import { ActiveDropAction } from "@/types/dropInteractionTypes";
import { MentionNode } from "../drops/create/lexical/nodes/MentionNode";
import { GroupMentionNode } from "../drops/create/lexical/nodes/GroupMentionNode";
import { HashtagNode } from "../drops/create/lexical/nodes/HashtagNode";
import { WaveMentionNode } from "../drops/create/lexical/nodes/WaveMentionNode";
import { ImageNode } from "../drops/create/lexical/nodes/ImageNode";
import ExampleTheme from "../drops/create/lexical/ExampleTheme";
import { assertUnreachable } from "@/helpers/AllowlistToolHelpers";
import type { ClearEditorPluginHandles } from "../drops/create/lexical/plugins/ClearEditorPlugin";
import ClearEditorPlugin from "../drops/create/lexical/plugins/ClearEditorPlugin";
import type { NewMentionsPluginHandles } from "../drops/create/lexical/plugins/mentions/MentionsPlugin";
import NewMentionsPlugin from "../drops/create/lexical/plugins/mentions/MentionsPlugin";
import type { NewHastagsPluginHandles } from "../drops/create/lexical/plugins/hashtags/HashtagsPlugin";
import NewHashtagsPlugin from "../drops/create/lexical/plugins/hashtags/HashtagsPlugin";
import type { NewWaveMentionsPluginHandles } from "../drops/create/lexical/plugins/waves/WaveMentionsPlugin";
import NewWaveMentionsPlugin from "../drops/create/lexical/plugins/waves/WaveMentionsPlugin";
import { MaxLengthPlugin } from "../drops/create/lexical/plugins/MaxLengthPlugin";
import DragDropPastePlugin from "../drops/create/lexical/plugins/DragDropPastePlugin";
import EnterKeyPlugin from "../drops/create/lexical/plugins/enter/EnterKeyPlugin";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import CreateDropEmojiPicker from "./CreateDropEmojiPicker";
import useCapacitor from "@/hooks/useCapacitor";
import EmojiPlugin from "../drops/create/lexical/plugins/emoji/EmojiPlugin";
import { EmojiNode } from "../drops/create/lexical/nodes/EmojiNode";
import { SAFE_MARKDOWN_TRANSFORMERS } from "@/components/drops/create/lexical/transformers/markdownTransformers";
import PlainTextPastePlugin from "@/components/drops/create/lexical/plugins/PlainTextPastePlugin";
export interface CreateDropInputHandles {
  clearEditorState: () => void;
  focus: () => void;
}

// Create a custom command
const DISABLE_EDIT_COMMAND = createCommand("DISABLE_EDIT");

// Create a custom plugin to handle disabling
function DisableEditPlugin(t0) {
  const $ = _c(4);
  const {
    disabled
  } = t0;
  const [editor] = useLexicalComposerContext();
  let t1;
  let t2;
  if ($[0] !== disabled || $[1] !== editor) {
    t1 = () => {
      if (disabled) {
        return editor.registerCommand(DISABLE_EDIT_COMMAND, _temp, COMMAND_PRIORITY_CRITICAL);
      }
      return _temp2;
    };
    t2 = [editor, disabled];
    $[0] = disabled;
    $[1] = editor;
    $[2] = t1;
    $[3] = t2;
  } else {
    t1 = $[2];
    t2 = $[3];
  }
  useEffect(t1, t2);
  return null;
}
function _temp2() {}
function _temp() {
  return true;
}
const CreateDropInput = forwardRef<CreateDropInputHandles, {
  readonly waveId: string;
  readonly editorState: EditorState | null;
  readonly type: ActiveDropAction | null;
  readonly canSubmit: boolean;
  readonly isStormMode: boolean;
  readonly submitting: boolean;
  readonly isDropMode: boolean;
  readonly canMentionAll?: boolean | undefined;
  readonly onDrop?: (() => void) | undefined;
  readonly onEditorState: (editorState: EditorState) => void;
  readonly onEditorBlur?: (event: FocusEvent<HTMLDivElement>) => void;
  readonly onReferencedNft: (referencedNft: ReferencedNft) => void;
  readonly onMentionedUser: (mentionedUser: Omit<MentionedUser, "current_handle">) => void;
  readonly onMentionedWave: (mentionedWave: MentionedWave) => void;
  readonly onAttachmentFiles?: ((files: File[]) => void) | undefined;
  readonly hasValidationError?: boolean | undefined;
  readonly validationHelperText?: string | null | undefined;
}>((t0, ref) => {
  const $ = _c(87);
  const {
    waveId,
    editorState,
    type,
    canSubmit,
    isStormMode,
    isDropMode,
    canMentionAll: t1,
    submitting,
    onEditorState,
    onEditorBlur,
    onReferencedNft,
    onMentionedUser,
    onMentionedWave,
    onAttachmentFiles,
    hasValidationError: t2,
    validationHelperText: t3,
    onDrop
  } = t0;
  const canMentionAll = t1 === undefined ? false : t1;
  const hasValidationError = t2 === undefined ? false : t2;
  const validationHelperText = t3 === undefined ? null : t3;
  const {
    isCapacitor
  } = useCapacitor();
  let t4;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = [MentionNode, GroupMentionNode, HashtagNode, WaveMentionNode, RootNode, HeadingNode, ListNode, ListItemNode, QuoteNode, CodeNode, CodeHighlightNode, TableNode, TableCellNode, TableRowNode, AutoLinkNode, LinkNode, HorizontalRuleNode, ImageNode, EmojiNode];
    $[0] = t4;
  } else {
    t4 = $[0];
  }
  const t5 = !submitting;
  let t6;
  if ($[1] !== editorState || $[2] !== t5) {
    t6 = {
      namespace: "User Drop",
      nodes: t4,
      editorState,
      editable: t5,
      onError(error) {
        throw error;
      },
      theme: ExampleTheme
    };
    $[1] = editorState;
    $[2] = t5;
    $[3] = t6;
  } else {
    t6 = $[3];
  }
  const editorConfig = t6;
  let t7;
  if ($[4] !== onEditorState) {
    t7 = editorState_0 => onEditorState(editorState_0);
    $[4] = onEditorState;
    $[5] = t7;
  } else {
    t7 = $[5];
  }
  const onEditorStateChange = t7;
  let t8;
  if ($[6] !== onMentionedUser) {
    t8 = user => {
      onMentionedUser(user);
    };
    $[6] = onMentionedUser;
    $[7] = t8;
  } else {
    t8 = $[7];
  }
  const onMentionedUserAdded = t8;
  let t9;
  if ($[8] !== onMentionedWave) {
    t9 = wave => {
      onMentionedWave(wave);
    };
    $[8] = onMentionedWave;
    $[9] = t9;
  } else {
    t9 = $[9];
  }
  const onMentionedWaveAdded = t9;
  let t10;
  if ($[10] !== onReferencedNft) {
    t10 = hashtag => onReferencedNft(hashtag);
    $[10] = onReferencedNft;
    $[11] = t10;
  } else {
    t10 = $[11];
  }
  const onHashtagAdded = t10;
  let t11;
  if ($[12] !== isDropMode || $[13] !== isStormMode || $[14] !== type) {
    t11 = () => {
      if (isStormMode) {
        return "Add to the storm";
      }
      if (type === null) {
        return isDropMode ? "Create a drop" : "Create a post";
      }
      switch (type) {
        case ActiveDropAction.REPLY:
          {
            return isDropMode ? "Drop a reply" : "Post a reply";
          }
        case ActiveDropAction.QUOTE:
          {
            return isDropMode ? "Quote a drop" : "Post a quote";
          }
        default:
          {
            assertUnreachable(type);
            return "";
          }
      }
    };
    $[12] = isDropMode;
    $[13] = isStormMode;
    $[14] = type;
    $[15] = t11;
  } else {
    t11 = $[15];
  }
  const getPlaceHolderText = t11;
  let t12;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    const urlRegExp = new RegExp(/((([A-Za-z]{3,9}:(?:\/\/)?)(?:[-;:&=+$,\w]+@)?[A-Za-z0-9.-]+|(?:www.|[-;:&=+$,\w]+@)[A-Za-z0-9.-]+)((?:\/[+~%/.\w-_]*)?\??(?:[-+=&;%@.\w_]*)#?(?:[\w]*))?)/);
    t12 = function validateUrl(url) {
      return url === "https://" || urlRegExp.test(url);
    };
    $[16] = t12;
  } else {
    t12 = $[16];
  }
  const validateUrl = t12;
  const clearEditorRef = useRef(null);
  const editorRef = useRef(null);
  let t13;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = () => {
      clearEditorRef.current?.clearEditorState();
    };
    $[17] = t13;
  } else {
    t13 = $[17];
  }
  const clearEditorState = t13;
  let t14;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = () => ({
      clearEditorState,
      focus: () => {
        (editorRef.current?.querySelector("[contenteditable=\"true\"]") as HTMLElement).focus();
      }
    });
    $[18] = t14;
  } else {
    t14 = $[18];
  }
  useImperativeHandle(ref, t14);
  const mentionsPluginRef = useRef(null);
  let t15;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = () => !!mentionsPluginRef.current?.isMentionsOpen();
    $[19] = t15;
  } else {
    t15 = $[19];
  }
  const isMentionsOpen = t15;
  const hashtagPluginRef = useRef(null);
  let t16;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = () => !!hashtagPluginRef.current?.isHashtagsOpen();
    $[20] = t16;
  } else {
    t16 = $[20];
  }
  const isHashtagsOpen = t16;
  const waveMentionsPluginRef = useRef(null);
  let t17;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    const isWaveMentionsOpen = () => !!waveMentionsPluginRef.current?.isWaveMentionsOpen();
    t17 = () => !isMentionsOpen() && !isHashtagsOpen() && !isWaveMentionsOpen();
    $[21] = t17;
  } else {
    t17 = $[21];
  }
  const canSubmitWithEnter = t17;
  const canSubmitRef = useRef(canSubmit);
  const onDropRef = useRef(onDrop);
  let t18;
  let t19;
  if ($[22] !== canSubmit) {
    t18 = () => {
      canSubmitRef.current = canSubmit;
    };
    t19 = [canSubmit];
    $[22] = canSubmit;
    $[23] = t18;
    $[24] = t19;
  } else {
    t18 = $[23];
    t19 = $[24];
  }
  useEffect(t18, t19);
  let t20;
  let t21;
  if ($[25] !== onDrop) {
    t20 = () => {
      onDropRef.current = onDrop;
    };
    t21 = [onDrop];
    $[25] = onDrop;
    $[26] = t20;
    $[27] = t21;
  } else {
    t20 = $[26];
    t21 = $[27];
  }
  useEffect(t20, t21);
  let t22;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = () => {
      if (!canSubmitRef.current || !onDropRef.current) {
        return;
      }
      onDropRef.current();
    };
    $[28] = t22;
  } else {
    t22 = $[28];
  }
  const handleSubmit = t22;
  let t23;
  if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = {
      touchAction: "manipulation"
    };
    $[29] = t23;
  } else {
    t23 = $[29];
  }
  let t24;
  if ($[30] !== submitting) {
    t24 = e => {
      const target = e.currentTarget;
      if (!submitting) {
        Promise.resolve().then(() => {
          target.focus();
          const selection = window.getSelection();
          if (selection?.rangeCount === 0) {
            const range = document.createRange();
            range.selectNodeContents(target);
            range.collapse(false);
            selection.addRange(range);
          }
        });
      }
    };
    $[30] = submitting;
    $[31] = t24;
  } else {
    t24 = $[31];
  }
  const t25 = `editor-input-one-liner tw-form-input tw-block tw-max-h-[40vh] tw-w-full tw-resize-none tw-rounded-lg tw-border-0 tw-bg-iron-900 tw-py-2.5 tw-pl-3 tw-text-base tw-font-normal tw-leading-6 tw-text-white tw-caret-primary-400 tw-shadow-sm tw-ring-1 tw-ring-inset tw-ring-iron-700 tw-transition tw-duration-300 tw-ease-out tw-scrollbar-thin tw-scrollbar-track-iron-900 tw-scrollbar-thumb-iron-600 placeholder:tw-text-iron-500 focus:tw-bg-iron-950 focus:tw-outline-none focus:tw-ring-1 focus:tw-ring-inset focus:tw-ring-primary-400 sm:tw-text-sm ${submitting ? "tw-cursor-default tw-opacity-50" : ""} ${isCapacitor ? "tw-pr-[35px]" : "tw-pr-[40px]"}`;
  let t26;
  if ($[32] !== onEditorBlur || $[33] !== t24 || $[34] !== t25) {
    t26 = <ContentEditable spellCheck={true} autoCorrect="on" style={t23} onClick={t24} onBlur={onEditorBlur} className={t25} />;
    $[32] = onEditorBlur;
    $[33] = t24;
    $[34] = t25;
    $[35] = t26;
  } else {
    t26 = $[35];
  }
  let t27;
  if ($[36] === Symbol.for("react.memo_cache_sentinel")) {
    t27 = <CreateDropEmojiPicker />;
    $[36] = t27;
  } else {
    t27 = $[36];
  }
  let t28;
  if ($[37] !== t26) {
    t28 = <div className="tw-relative">{t26}{t27}</div>;
    $[37] = t26;
    $[38] = t28;
  } else {
    t28 = $[38];
  }
  const t29 = `editor-placeholder ${submitting ? "tw-opacity-50" : ""}`;
  const t30 = getPlaceHolderText();
  let t31;
  if ($[39] !== t29 || $[40] !== t30) {
    t31 = <span className={t29}>{t30}</span>;
    $[39] = t29;
    $[40] = t30;
    $[41] = t31;
  } else {
    t31 = $[41];
  }
  let t32;
  if ($[42] !== t28 || $[43] !== t31) {
    t32 = <RichTextPlugin contentEditable={t28} placeholder={t31} ErrorBoundary={LexicalErrorBoundary} />;
    $[42] = t28;
    $[43] = t31;
    $[44] = t32;
  } else {
    t32 = $[44];
  }
  let t33;
  if ($[45] === Symbol.for("react.memo_cache_sentinel")) {
    t33 = <HistoryPlugin />;
    $[45] = t33;
  } else {
    t33 = $[45];
  }
  let t34;
  if ($[46] !== onEditorStateChange) {
    t34 = <OnChangePlugin onChange={onEditorStateChange} />;
    $[46] = onEditorStateChange;
    $[47] = t34;
  } else {
    t34 = $[47];
  }
  let t35;
  if ($[48] !== canMentionAll || $[49] !== onMentionedUserAdded || $[50] !== waveId) {
    t35 = <NewMentionsPlugin waveId={waveId} onSelect={onMentionedUserAdded} canMentionAll={canMentionAll} ref={mentionsPluginRef} />;
    $[48] = canMentionAll;
    $[49] = onMentionedUserAdded;
    $[50] = waveId;
    $[51] = t35;
  } else {
    t35 = $[51];
  }
  let t36;
  if ($[52] !== onMentionedWaveAdded) {
    t36 = <NewWaveMentionsPlugin onSelect={onMentionedWaveAdded} ref={waveMentionsPluginRef} />;
    $[52] = onMentionedWaveAdded;
    $[53] = t36;
  } else {
    t36 = $[53];
  }
  let t37;
  if ($[54] !== onHashtagAdded) {
    t37 = <NewHashtagsPlugin onSelect={onHashtagAdded} ref={hashtagPluginRef} />;
    $[54] = onHashtagAdded;
    $[55] = t37;
  } else {
    t37 = $[55];
  }
  let t38;
  if ($[56] === Symbol.for("react.memo_cache_sentinel")) {
    t38 = <MaxLengthPlugin maxLength={25000} />;
    $[56] = t38;
  } else {
    t38 = $[56];
  }
  let t39;
  if ($[57] !== onAttachmentFiles) {
    t39 = <DragDropPastePlugin onAttachmentFiles={onAttachmentFiles} />;
    $[57] = onAttachmentFiles;
    $[58] = t39;
  } else {
    t39 = $[58];
  }
  let t40;
  let t41;
  let t42;
  let t43;
  let t44;
  let t45;
  if ($[59] === Symbol.for("react.memo_cache_sentinel")) {
    t40 = <ListPlugin />;
    t41 = <PlainTextPastePlugin />;
    t42 = <MarkdownShortcutPlugin transformers={SAFE_MARKDOWN_TRANSFORMERS} />;
    t43 = <TabIndentationPlugin />;
    t44 = <LinkPlugin validateUrl={validateUrl} />;
    t45 = <ClearEditorPlugin ref={clearEditorRef} />;
    $[59] = t40;
    $[60] = t41;
    $[61] = t42;
    $[62] = t43;
    $[63] = t44;
    $[64] = t45;
  } else {
    t40 = $[59];
    t41 = $[60];
    t42 = $[61];
    t43 = $[62];
    t44 = $[63];
    t45 = $[64];
  }
  let t46;
  let t47;
  if ($[65] !== submitting) {
    t46 = <DisableEditPlugin disabled={submitting} />;
    t47 = <EnterKeyPlugin handleSubmit={handleSubmit} canSubmitWithEnter={canSubmitWithEnter} disabled={submitting} />;
    $[65] = submitting;
    $[66] = t46;
    $[67] = t47;
  } else {
    t46 = $[66];
    t47 = $[67];
  }
  let t48;
  if ($[68] === Symbol.for("react.memo_cache_sentinel")) {
    t48 = <EmojiPlugin />;
    $[68] = t48;
  } else {
    t48 = $[68];
  }
  let t49;
  if ($[69] !== t32 || $[70] !== t34 || $[71] !== t35 || $[72] !== t36 || $[73] !== t37 || $[74] !== t39 || $[75] !== t46 || $[76] !== t47) {
    t49 = <div className="tw-flex tw-items-end tw-gap-x-3"><div className="tw-relative tw-w-full">{t32}{t33}{t34}{t35}{t36}{t37}{t38}{t39}{t40}{t41}{t42}{t43}{t44}{t45}{t46}{t47}{t48}</div></div>;
    $[69] = t32;
    $[70] = t34;
    $[71] = t35;
    $[72] = t36;
    $[73] = t37;
    $[74] = t39;
    $[75] = t46;
    $[76] = t47;
    $[77] = t49;
  } else {
    t49 = $[77];
  }
  let t50;
  if ($[78] !== editorConfig || $[79] !== t49) {
    t50 = <LexicalComposer initialConfig={editorConfig}>{t49}</LexicalComposer>;
    $[78] = editorConfig;
    $[79] = t49;
    $[80] = t50;
  } else {
    t50 = $[80];
  }
  let t51;
  if ($[81] !== hasValidationError || $[82] !== validationHelperText) {
    t51 = hasValidationError && validationHelperText && <div role="alert" className="tw-mt-2 tw-text-xs tw-font-medium tw-text-error">{validationHelperText}</div>;
    $[81] = hasValidationError;
    $[82] = validationHelperText;
    $[83] = t51;
  } else {
    t51 = $[83];
  }
  let t52;
  if ($[84] !== t50 || $[85] !== t51) {
    t52 = <div className="tailwind-scope" ref={editorRef}>{t50}{t51}</div>;
    $[84] = t50;
    $[85] = t51;
    $[86] = t52;
  } else {
    t52 = $[86];
  }
  return t52;
});
CreateDropInput.displayName = "CreateDropInput";
export default CreateDropInput;